import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DangerZoneButton
 * ----------------
 * Temporary Super-Admin-only destructive-action button. Wraps:
 *   1. A red trigger button (label + data-testid from props)
 *   2. A confirmation modal that requires the user to type CLEAR
 *   3. A POST to the given endpoint with {"confirm": "CLEAR"}
 *   4. Toast notifications for outcome
 *   5. Optional onSuccess callback for caller-side list refresh
 *
 * Remove this component (and its imports) when the Danger Zone buttons are
 * retired per the platform owner's instructions.
 */
export default function DangerZoneButton({
  label,
  endpoint,            // e.g. '/api/admin/danger/clear-support-tickets'
  confirmTitle,
  confirmDescription,  // JSX or string — what will be deleted
  testId,
  onSuccess,
  disabled = false,
}) {
  const { authToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);

  const armed = typed.trim() === 'CLEAR';

  const handleConfirm = async () => {
    if (!armed || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ confirm: 'CLEAR' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json.detail || 'Clear failed', { duration: 6000 });
        return;
      }
      const total = json.total ?? 0;
      if (total === 0) {
        toast.info('Nothing to clear — collection is already empty.');
      } else {
        const collections = Object.entries(json.deleted || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        toast.success(`Deleted ${total} rows`, {
          description: collections,
          duration: 8000,
        });
      }
      setOpen(false);
      setTyped('');
      if (onSuccess) onSuccess(json);
    } catch (e) {
      toast.error(`Network error: ${e.message || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid={testId}
      >
        <AlertTriangle className="w-4 h-4 mr-2" />
        {label}
      </Button>

      <AlertDialog
        open={open}
        onOpenChange={(v) => {
          if (!loading) {
            setOpen(v);
            if (!v) setTyped('');
          }
        }}
      >
        <AlertDialogContent data-testid={`${testId}-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              {confirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-3 mt-2">
                <div>{confirmDescription}</div>
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-xs">
                  This action is <strong>irreversible</strong>. Deleted data cannot be recovered.
                  The action will be recorded in the audit log under your user.
                </div>
                <div>
                  <Label htmlFor={`${testId}-input`} className="text-xs font-semibold text-slate-700">
                    Type <code className="bg-slate-100 px-1 rounded font-mono">CLEAR</code> to enable the delete button:
                  </Label>
                  <Input
                    id={`${testId}-input`}
                    data-testid={`${testId}-input`}
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="CLEAR"
                    className="mt-1 font-mono"
                    autoFocus
                    disabled={loading}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading} data-testid={`${testId}-cancel`}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={!armed || loading}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              data-testid={`${testId}-confirm`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete now'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
